import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import {
  ComponentFieldValuesMap,
  FieldValues,
} from 'features/libra/ontology/ontologyFieldTypes'
import type { GeckoEntry, GeckoInspection } from 'types'

/**
 * Pure helpers for the "Select Inspections" gating step (`FfsInspectionSelectionStep`).
 * The step persists one pinned inspection per component under
 * `fieldValues.components[componentId][SELECTED_INSPECTION_KEY]` so FFS calculations
 * read frozen data instead of `TabbedWorkspace`'s live auto-pick.
 */

export const SELECTED_INSPECTION_KEY = 'selectedInspectionId'

/** Unwraps the persisted pin off one component's field-value slice. */
export function readSelectedInspectionId(
  componentFields: FieldValues | undefined
): string | undefined {
  return unwrapFieldValue<string>(componentFields?.[SELECTED_INSPECTION_KEY])
}

/**
 * `componentId -> Set<inspectionId>` membership, built once per call so both
 * `resolvePinnedInspections` and `isFullyPinned` validate a pin against the
 * SAME component (a stale/retagged inspection id must never be accepted just
 * because it exists somewhere in the pool).
 */
function indexInspectionsByComponent(
  inspectionEntries: readonly GeckoEntry[]
): Map<string, Set<string>> {
  const index = new Map<string, Set<string>>()
  for (const entry of inspectionEntries) {
    const componentId = entry.tags.component
    if (!componentId) continue
    const set = index.get(componentId)
    if (set) {
      set.add(entry.id)
    } else {
      index.set(componentId, new Set([entry.id]))
    }
  }
  return index
}

/**
 * Resolve the inspection each component should use: the persisted pin when
 * it is still a valid inspection for that component, otherwise the live
 * auto-pick (`autoMap`, from `buildInspectionsByComponentId`). A component
 * absent from both is omitted, matching `autoMap`'s "no inspection -> no
 * entry" contract.
 */
export function resolvePinnedInspections(
  autoMap: Record<string, string>,
  componentSubtree: ComponentFieldValuesMap | undefined,
  components: readonly { id: string }[],
  inspectionEntries: readonly GeckoEntry[]
): Record<string, string> {
  const byComponent = indexInspectionsByComponent(inspectionEntries)
  const out: Record<string, string> = {}
  for (const component of components) {
    const pinned = readSelectedInspectionId(componentSubtree?.[component.id])
    const isPinnedValid =
      pinned !== undefined && !!byComponent.get(component.id)?.has(pinned)
    const resolved = isPinnedValid ? pinned : autoMap[component.id]
    if (resolved !== undefined) {
      out[component.id] = resolved
    }
  }
  return out
}

/**
 * Gate for the step: true once every component that has at least one
 * inspection carries a valid persisted pin. A component with zero
 * inspections is excluded from FFS entirely, so it can never block
 * completion.
 */
export function isFullyPinned(
  components: readonly { id: string }[],
  componentSubtree: ComponentFieldValuesMap | undefined,
  inspectionEntries: readonly GeckoEntry[]
): boolean {
  const byComponent = indexInspectionsByComponent(inspectionEntries)
  return components.every((component) => {
    const validInspectionIds = byComponent.get(component.id)
    if (!validInspectionIds) return true
    const pinned = readSelectedInspectionId(componentSubtree?.[component.id])
    return pinned !== undefined && validInspectionIds.has(pinned)
  })
}

export interface ComponentInspectionOption {
  inspectionId: string
  label: string
  hasData: boolean
  isRecommended: boolean
}

/**
 * Mirrors the (unexported) label format in
 * `dataLayers/generation/shortTermCorrosionRate/inspectionChoices.ts`
 * (`date · vendor · name · type`) without exporting it there, since that
 * file is owned by another team (CODEOWNERS: femr-team) and out of scope.
 */
function formatInspectionLabel(inspection: GeckoInspection): string {
  const date = inspection.data.date.toISOString().slice(0, 10)
  const descriptor = [
    inspection.data.vendor,
    inspection.data.name,
    inspection.data.inspection_type,
  ]
    .filter(Boolean)
    .join(' · ')
  return descriptor ? `${date} · ${descriptor}` : `${date} · ${inspection.id}`
}

/**
 * This component's inspections, newest-first, each flagged for whether it
 * carries CDF data (`inspectionsWithData`, from `buildInspectionsWithCdfData`)
 * and whether it is the live auto-pick (`recommendedId`).
 */
export function listComponentInspectionOptions(
  componentId: string,
  inspections: readonly GeckoInspection[],
  inspectionsWithData: Map<string, Set<string>>,
  recommendedId: string | undefined
): ComponentInspectionOption[] {
  const dataInspectionIds = inspectionsWithData.get(componentId)
  return inspections
    .filter((inspection) => inspection.tags.component === componentId)
    .toSorted((a, b) => {
      const aTime = a.data.date.getTime()
      const bTime = b.data.date.getTime()
      const aValid = !Number.isNaN(aTime)
      const bValid = !Number.isNaN(bTime)
      if (aValid !== bValid) return aValid ? -1 : 1
      return bTime - aTime
    })
    .map((inspection) => ({
      inspectionId: inspection.id,
      label: formatInspectionLabel(inspection),
      hasData: dataInspectionIds?.has(inspection.id) ?? false,
      isRecommended: inspection.id === recommendedId,
    }))
}
